import { createI18n } from 'vue-i18n';
import { computed } from 'vue';
import zhTw from './lang/zh-tw';
import en from './lang/en';
import elementZhTw from 'element-plus/es/locale/lang/zh-tw';
import elementEn from 'element-plus/es/locale/lang/en';

const LANGUAGE_KEY = 'app-language';
const CACHE_KEY = 'i18n-cache';

export const systemLanguageMap = {
  'zh-TW': zhTw,
  en: en,
};

const elementPlusLocaleMap = {
  'zh-TW': elementZhTw,
  en: elementEn,
};

export const languageOptions = [
  { value: 'zh-TW', label: '繁體中文' },
  { value: 'en', label: '英语' },
];

const cache = JSON.parse(localStorage.getItem(CACHE_KEY) || '{}');
const pending = new Set();
const MAX_CACHE_SIZE = 1000;

function mergeCacheToMessages(locale) {
  const baseMessages = { ...systemLanguageMap[locale] };
  const localePrefix = `${locale}:`;

  for (const cacheKey in cache) {
    if (cacheKey.startsWith(localePrefix)) {
      const key = cacheKey.slice(localePrefix.length);
      if (key && !(key in baseMessages)) {
        baseMessages[key] = cache[cacheKey];
      }
    }
  }

  return baseMessages;
}

function cleanupCache() {
  const entries = Object.entries(cache);
  if (entries.length > MAX_CACHE_SIZE) {
    const toDelete = entries.slice(0, entries.length - MAX_CACHE_SIZE);
    toDelete.forEach(([key]) => delete cache[key]);
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    if (import.meta.env.DEV) {
      console.log(`[i18n] 缓存已清理，删除了 ${toDelete.length} 条旧缓存`);
    }
  }
}

let i18nInstance = null;

const translateQueue = new Map();
let translateTimer = null;
let updateTimer = null;
const TRANSLATE_DELAY = 500;
const UPDATE_DELAY = 500;
const MAX_BATCH_SIZE = 50;

const pendingUpdates = new Map();

async function translateBatch(texts, locale) {
  if (locale !== 'en') return texts;

  if (texts.length === 0) return [];

  try {
    const url = 'https://api.seeronnet.com/v1/common/google/translation';
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        format: 'text',
        lang: 'en',
        text: texts,
      }),
    });

    if (!res.ok) {
      console.warn('[i18n] 翻译请求失败:', res.status, res.statusText);
      return texts;
    }

    const data = await res.json();

    if (import.meta.env.DEV) {
      console.log('[i18n] 批量翻译接口返回:', data);
    }

    if (data && data.code === 200 && Array.isArray(data.data) && data.data.length > 0) {
      return data.data.map((item, index) => {
        if (item && item.translatedText && typeof item.translatedText === 'string') {
          return item.translatedText;
        }
        return texts[index] || '';
      });
    }

    console.warn('[i18n] 翻译返回格式异常或翻译失败:', data);
    return texts;
  } catch (e) {
    console.warn('[i18n] 批量翻译失败:', e);
    return texts;
  }
}

function batchUpdateMessages() {
  if (pendingUpdates.size === 0 || !i18nInstance) return;

  pendingUpdates.forEach((updates, locale) => {
    if (updates.size === 0) return;

    const currentMessages = i18nInstance.global.messages.value[locale] || {};
    const newMessages = { ...currentMessages };
    updates.forEach((value, key) => {
      newMessages[key] = value;
    });

    i18nInstance.global.setLocaleMessage(locale, newMessages);
    updates.clear();
  });

  pendingUpdates.clear();
}

function scheduleUpdate(locale, key, value) {
  if (!pendingUpdates.has(locale)) {
    pendingUpdates.set(locale, new Map());
  }
  pendingUpdates.get(locale).set(key, value);

  if (updateTimer) {
    clearTimeout(updateTimer);
  }

  updateTimer = setTimeout(() => {
    updateTimer = null;
    batchUpdateMessages();
  }, UPDATE_DELAY);
}

async function processTranslateQueue() {
  if (translateQueue.size === 0) return;

  const queueByLocale = new Map();

  translateQueue.forEach((item, cacheKey) => {
    const { locale, key, text, resolve, reject } = item;
    if (!queueByLocale.has(locale)) {
      queueByLocale.set(locale, []);
    }
    queueByLocale.get(locale).push({ cacheKey, key, text, resolve, reject });
  });

  translateQueue.clear();

  for (const [, items] of queueByLocale) {
    for (let i = 0; i < items.length; i += MAX_BATCH_SIZE) {
      const batch = items.slice(i, i + MAX_BATCH_SIZE);
      const texts = batch.map((item) => item.text);

      try {
        const results = await translateBatch(texts, 'en');
        const cacheUpdates = {};
        const successfulTranslations = [];

        batch.forEach((item, index) => {
          const result = results[index] || item.text;
          const { cacheKey, key } = item;

          if (result !== item.text) {
            cacheUpdates[cacheKey] = result;
            successfulTranslations.push({ key, result });
          } else {
            successfulTranslations.push({ key, result: key });
          }

          pending.delete(cacheKey);

          if (item.resolve) {
            item.resolve(result);
          }
        });

        if (Object.keys(cacheUpdates).length > 0) {
          Object.assign(cache, cacheUpdates);
          localStorage.setItem(CACHE_KEY, JSON.stringify(cache));

          if (Object.keys(cache).length > MAX_CACHE_SIZE) {
            cleanupCache();
          }

          if (import.meta.env.DEV) {
            console.log(`[i18n] ✅ 批量翻译完成: ${successfulTranslations.length} 条`);
          }
        }

        successfulTranslations.forEach(({ key, result }) => {
          scheduleUpdate('en', key, result);
        });
      } catch (error) {
        batch.forEach((item) => {
          const { cacheKey, key, reject } = item;
          pending.delete(cacheKey);
          scheduleUpdate('en', key, key);
          if (reject) {
            reject(error);
          }
        });
      }
    }
  }
}

function addToTranslateQueue(key, text, locale) {
  return new Promise((resolve, reject) => {
    const cacheKey = `${locale}:${key}`;
    translateQueue.set(cacheKey, {
      key,
      text,
      locale,
      resolve,
      reject,
    });

    if (translateTimer) {
      clearTimeout(translateTimer);
    }

    translateTimer = setTimeout(() => {
      translateTimer = null;
      processTranslateQueue();
    }, TRANSLATE_DELAY);

    if (translateQueue.size >= MAX_BATCH_SIZE) {
      if (translateTimer) {
        clearTimeout(translateTimer);
        translateTimer = null;
      }
      processTranslateQueue();
    }
  });
}

function handleMissing(locale, key) {
  const normalizedLocale = locale === 'zh' ? 'zh-TW' : locale;

  if (normalizedLocale !== 'en' || !key) {
    return key;
  }

  const systemMessages = systemLanguageMap[normalizedLocale];
  if (systemMessages && key in systemMessages) {
    if (import.meta.env.DEV) {
      console.warn(`[i18n] 警告：语言库中已有配置 "${key}"，但 handleMissing 被调用`);
    }
    return systemMessages[key];
  }

  const cacheKey = `${normalizedLocale}:${key}`;

  if (cache[cacheKey]) {
    if (import.meta.env.DEV) {
      console.log(`[i18n] 使用缓存: "${key}" → "${cache[cacheKey]}"`);
    }
    return cache[cacheKey];
  }

  if (pending.has(cacheKey)) {
    return '...';
  }

  pending.add(cacheKey);

  if (import.meta.env.DEV) {
    console.warn(`[i18n] 缺失翻译: "${key}"，已加入翻译队列...`);
  }

  scheduleUpdate(normalizedLocale, key, '...');

  addToTranslateQueue(key, key, normalizedLocale).catch((err) => {
    if (import.meta.env.DEV) {
      console.error(`[i18n] 翻译异常: "${key}"`, err);
    }
    scheduleUpdate(normalizedLocale, key, key);
    pending.delete(cacheKey);
  });

  return '...';
}

export function getLanguage() {
  const saved = localStorage.getItem(LANGUAGE_KEY);
  return saved && systemLanguageMap[saved] ? saved : 'zh-TW';
}

export function setLanguage(lang) {
  if (systemLanguageMap[lang]) {
    localStorage.setItem(LANGUAGE_KEY, lang);

    const mergedMessages = mergeCacheToMessages(lang);
    i18n.global.setLocaleMessage(lang, mergedMessages);

    i18n.global.locale.value = lang;
  }
}

export function exportCache() {
  const result = {};
  Object.entries(cache).forEach(([k, v]) => {
    const [locale, text] = k.split(':');
    if (!result[locale]) result[locale] = {};
    result[locale][text] = v;
  });
  console.log('[i18n] 缓存:', JSON.stringify(result, null, 2));
  return result;
}

export function copyToLangFile() {
  const enCache = {};

  Object.entries(cache).forEach(([k, v]) => {
    const [locale, text] = k.split(':');
    if (locale === 'en' && text) {
      enCache[text] = v;
    }
  });

  if (Object.keys(enCache).length === 0) {
    console.warn('[i18n] ⚠️ 没有英文缓存可导出');
    return '';
  }

  const existing = systemLanguageMap.en || {};
  const merged = { ...existing, ...enCache };

  function escapeString(str) {
    if (typeof str !== 'string') return str;
    return str
      .replace(/\\/g, '\\\\')
      .replace(/'/g, "\\'")
      .replace(/\n/g, '\\n')
      .replace(/\r/g, '\\r')
      .replace(/\t/g, '\\t');
  }

  const entries = Object.entries(merged)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => {
      const escapedValue = escapeString(value);
      return `  ${key}: '${escapedValue}',`;
    });

  const code = `export default {\n${entries.join('\n')}\n};`;

  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard
      .writeText(code)
      .then(() => {
        console.log('[i18n] ✅ 已复制到剪贴板！');
        console.log('[i18n] 📝 请粘贴到 src/i18n/lang/en.js 文件中');
        console.log(
          `[i18n] 📊 共 ${Object.keys(merged).length} 条翻译（${Object.keys(enCache).length} 条来自缓存）`,
        );
        console.log('--- 代码开始 ---');
        console.log(code);
        console.log('--- 代码结束 ---');
      })
      .catch((err) => {
        console.error('[i18n] ❌ 复制失败:', err);
        console.log(code);
      });
  } else {
    console.log(code);
  }

  return code;
}

export function clearCache() {
  localStorage.removeItem(CACHE_KEY);
  Object.keys(cache).forEach((k) => delete cache[k]);
  console.log('[i18n] 缓存已清除');
}

const lang = getLanguage();
console.log('[i18n] 语言:', lang);

const initialMessages = {
  'zh-TW': mergeCacheToMessages('zh-TW'),
  en: mergeCacheToMessages('en'),
};

const i18n = createI18n({
  legacy: false,
  locale: lang,
  fallbackLocale: false,
  messages: initialMessages,
  silentTranslationWarn: true,
  silentFallbackWarn: true,
  fallbackWarn: false,
  missingWarn: false,
  missing: handleMissing,
});

i18nInstance = i18n;

export const elementPlusLocale = computed(() => {
  return elementPlusLocaleMap[i18n.global.locale.value] || elementZhTw;
});

export const t = (key, ...args) => {
  return i18n.global.t(key, ...args);
};

if (import.meta.env.DEV) {
  window.__i18n__ = {
    exportCache,
    copyToLangFile,
    clearCache,
    cache,
    t,
  };
  console.log('[i18n] 开发工具已加载，使用 __i18n__.copyToLangFile() 一键复制到语言库');
}

export default i18n;
